import { UploadOutlined } from '@ant-design/icons';
import {
  ATTACHMENT_BUCKET_LABEL_KEYS,
  ATTACHMENT_BUCKET_STYLE,
  type AttachmentKind,
  AttachmentKindEditable,
  AttachmentKindTag,
  attachmentBucketOf,
} from '@app/features/attachments/attachmentKind';
import type { AttachmentBase } from '@api/READ_ONLY/visits_api/Api';
import TableLink from '@app/components/common/TableLink';
import type { UploadFile, UploadProps } from 'antd';
import { Image, Progress, Spin, Upload, message } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { inDisplayZone } from '@app/utils/dates/displayTimezone';

// File validation constants
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB in bytes
const ALLOWED_FILE_TYPES = {
  // Images
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'image/bmp': ['.bmp'],
  'image/svg+xml': ['.svg'],
  // Documents
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],
};
const ALLOWED_EXTENSIONS = Object.values(ALLOWED_FILE_TYPES).flat();

export interface AttachmentManagerProps {
  attachments: AttachmentBase[];
  onDownload: (attachment: AttachmentBase) => void;
  onDelete?: (attachment: AttachmentBase) => void;
  previewUrls: Record<number, string | null>;
  ensurePreviewUrl: (attachment: AttachmentBase) => Promise<string | null>;
  onUpload?: UploadProps['customRequest'];
  deletingAttachmentId: number | null;
  uploadProps?: Partial<UploadProps>;
  className?: string;
  disabled?: boolean;
  inModal?: boolean;
  /** What the next upload is tagged as. Omit to leave the picker out entirely. */
  kindForUpload?: AttachmentKind | null;
  onKindForUploadChange?: (kind: AttachmentKind | null) => void;
  /** Re-tag an uploaded file. Omit and the kind is shown but not editable. */
  onRetag?: (attachment: AttachmentBase, kind: AttachmentKind | null) => void;
  retaggingAttachmentId?: number | null;
}

export interface AttachmentListItemProps {
  attachment: AttachmentBase;
  onDownload: (attachment: AttachmentBase) => void;
  onDelete?: (attachment: AttachmentBase) => void;
  previewUrls: Record<number, string | null>;
  ensurePreviewUrl: (attachment: AttachmentBase) => Promise<string | null>;
  deletingAttachmentId: number | null;
  onRetag?: (attachment: AttachmentBase, kind: AttachmentKind | null) => void;
  retaggingAttachmentId?: number | null;
}

/** Side of the square a row's preview occupies. */
const THUMB_SIZE = 120;

/**
 * What a file looks like before it is opened.
 *
 * Images show their own preview; everything else shows the icon and colour its kind
 * already has elsewhere, so a prescription reads the same in this list as in the
 * client card's folder. One fixed square either way, because a row that resizes when
 * a preview finishes loading moves the controls out from under the pointer.
 */
const AttachmentThumb: React.FC<{
  attachment: AttachmentBase;
  kind: string | null;
  src?: string;
}> = ({ attachment, kind, src }) => {
  const { t } = useTranslation();
  const isImage = !!attachment.contentType?.startsWith('image/');
  const box = { width: THUMB_SIZE, height: THUMB_SIZE };

  if (isImage && src) {
    return (
      <div className="shrink-0 overflow-hidden rounded-lg bg-slate-50" style={box}>
        <Image
          src={src}
          alt={attachment.filename}
          className="object-cover"
          style={{ ...box, objectFit: 'cover' }}
          preview={{ src }}
        />
      </div>
    );
  }

  const bucket = attachmentBucketOf({ kind, contentType: attachment.contentType });
  const style = ATTACHMENT_BUCKET_STYLE[bucket];

  return (
    <div
      className="flex shrink-0 flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed"
      style={{ ...box, background: style.bg, borderColor: `${style.color}55`, color: style.color }}
    >
      {isImage ? (
        <Spin size="small" />
      ) : (
        <>
          <span style={{ fontSize: 24 }}>{style.icon}</span>
          <span className="text-[10px] font-medium">{t(ATTACHMENT_BUCKET_LABEL_KEYS[bucket])}</span>
        </>
      )}
    </div>
  );
};

export const AttachmentListItem: React.FC<AttachmentListItemProps> = ({
  attachment,
  onDownload,
  onDelete,
  previewUrls,
  ensurePreviewUrl,
  deletingAttachmentId,
  onRetag,
  retaggingAttachmentId,
}) => {
  const { t } = useTranslation();
  const previewSrc = previewUrls[attachment.attachmentId] || null;
  // The API returns this; the generated AttachmentBase predates it.
  const kind = (attachment as AttachmentBase & { kind?: string | null }).kind ?? null;

  React.useEffect(() => {
    if (attachment.contentType?.startsWith('image/')) {
      void ensurePreviewUrl(attachment);
    }
  }, [attachment, attachment.contentType, ensurePreviewUrl]);

  const isDeleting = deletingAttachmentId === attachment.attachmentId;

  return (
    <div className="flex items-start gap-4 border-b border-slate-100 py-3 last:border-b-0">
      <AttachmentThumb attachment={attachment} kind={kind} src={previewSrc ?? undefined} />

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {/* Name, kind and actions share one line so the row has no gap down its
            middle: the name takes the slack, the rest sit against the right edge. */}
        <div className="flex items-start gap-3">
          <span className="min-w-0 flex-1 truncate font-medium text-slate-800" title={attachment.filename}>
            {attachment.filename}
          </span>

          {onRetag ? (
            <AttachmentKindEditable
              value={kind}
              onChange={(next) => onRetag(attachment, next)}
              saving={retaggingAttachmentId === attachment.attachmentId}
            />
          ) : (
            <AttachmentKindTag kind={kind} />
          )}

          <div className="flex shrink-0 items-center gap-3">
            <TableLink
              onClick={() => onDownload(attachment)}
              className={isDeleting ? 'pointer-events-none opacity-60' : undefined}
            >
              {t('common-download')}
            </TableLink>
            {onDelete &&
              (isDeleting ? (
                <Spin size="small" />
              ) : (
                <TableLink onClick={() => onDelete(attachment)}>{t('delete')}</TableLink>
              ))}
          </div>
        </div>

        <span className="text-xs text-slate-500">
          {inDisplayZone(attachment.uploadedAt).format('D MMM YYYY HH:mm')}
        </span>

        {attachment.description && (
          <span className="truncate text-xs text-slate-500" title={attachment.description}>
            {attachment.description}
          </span>
        )}
      </div>
    </div>
  );
};

export const AttachmentManager: React.FC<AttachmentManagerProps> = ({
  attachments,
  onDownload,
  onDelete,
  previewUrls,
  ensurePreviewUrl,
  onUpload,
  deletingAttachmentId,
  uploadProps,
  className,
  disabled = false,
  inModal = false,
  kindForUpload,
  onKindForUploadChange,
  onRetag,
  retaggingAttachmentId,
}) => {
  const { t } = useTranslation();
  const { Dragger } = Upload;
  const [uploadingFiles, setUploadingFiles] = React.useState<Record<string, { name: string; percent: number }>>({});

  const handleUploadChange: UploadProps['onChange'] = ({ file }) => {
    if (file.status === 'uploading') {
      setUploadingFiles((prev) => ({
        ...prev,
        [file.uid]: { name: file.name, percent: Math.round(file.percent ?? 0) },
      }));
    } else if (file.status === 'done' || file.status === 'error' || file.status === 'removed') {
      setUploadingFiles((prev) => {
        const next = { ...prev };
        delete next[file.uid];
        return next;
      });
    }
  };

  const beforeUpload = (file: UploadFile): boolean => {
    // Check file size
    if (file.size && file.size > MAX_FILE_SIZE) {
      void message.error(t('attach-error-file-too-large', { name: file.name }));
      return false;
    }

    // Check file type
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    const fileType = file.type;

    const isValidType = fileType && Object.keys(ALLOWED_FILE_TYPES).includes(fileType);
    const isValidExtension = ALLOWED_EXTENSIONS.includes(fileExtension);

    if (!isValidType && !isValidExtension) {
      void message.error(t('attach-error-file-type', { name: file.name }));
      return false;
    }

    return true;
  };

  const draggerProps: UploadProps | undefined = onUpload
    ? {
        multiple: true,
        showUploadList: false,
        customRequest: onUpload,
        onChange: handleUploadChange,
        beforeUpload,
        accept: ALLOWED_EXTENSIONS.join(','),
        ...uploadProps,
      }
    : undefined;

  const uploadsInProgress = Object.values(uploadingFiles);

  return (
    <div className={`w-full space-y-4 ${className}`}>
      {!disabled && onUpload && draggerProps && (
        <Dragger {...draggerProps} className="border-dashed">
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">{t('attach-upload-drag-text')}</p>
          <p className="ant-upload-hint text-xs text-gray-500">{t('attach-upload-hint')}</p>

          {onKindForUploadChange && (
            // Inside the drop zone, because it is part of the same decision as the
            // files: one kind for the whole drag, since dropping six lab results is
            // one decision and not six. Leaving it empty uploads them unclassified
            // rather than guessing a kind from the content type.
            //
            // The picker is inside the zone's own click target, so its clicks and keys
            // are stopped here - otherwise reaching for the dropdown opens the file
            // dialog instead.
            <div
              className="mt-4 flex flex-wrap items-center justify-center gap-2 border-t border-slate-200/70 pt-3"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <span className="text-xs text-slate-500">{t('attachment-kind-for-upload')}</span>
              <AttachmentKindEditable value={kindForUpload} onChange={onKindForUploadChange} />
            </div>
          )}
        </Dragger>
      )}

      {uploadsInProgress.length > 0 && (
        <div className="space-y-2">
          {uploadsInProgress.map(({ name, percent }) => (
            <div key={name} className="flex items-center gap-2">
              <span className="text-xs text-gray-600 w-40 truncate" title={name}>
                {name}
              </span>
              <Progress percent={percent} size="small" status="active" className="flex-1" />
            </div>
          ))}
        </div>
      )}

      {attachments.length === 0 ? (
        <div className="text-gray-500">{t('attach-no-attachments')}</div>
      ) : (
        <div className={`overflow-y-auto rounded-md ${inModal ? 'h-48 min-h-48 max-h-48' : 'max-h-130'}`}>
          <Image.PreviewGroup>
            {attachments.map((attachment) => (
              <AttachmentListItem
                key={attachment.attachmentId}
                attachment={attachment}
                onDownload={onDownload}
                onDelete={disabled ? undefined : onDelete}
                previewUrls={previewUrls}
                ensurePreviewUrl={ensurePreviewUrl}
                deletingAttachmentId={deletingAttachmentId}
                onRetag={disabled ? undefined : onRetag}
                retaggingAttachmentId={retaggingAttachmentId}
              />
            ))}
          </Image.PreviewGroup>
        </div>
      )}
    </div>
  );
};
